import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Spin } from 'antd';
import { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { ArrowDownSvg, ArrowLeftSvg, ArrowUpSvg, CrossSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { AgentQuestion, buildAgentQuestionsAnswerMessage } from './agentQuestionTools';

const OTHER_OPTION = '__other__';

export type AgentQuestionDrawerProps = {
  /**
   * Null renders a loading state: the tool call has started but its
   * arguments — the actual questions — haven't streamed in and parsed yet.
   */
  questions: AgentQuestion[] | null;
  /** Raw text for the next user message. Sent through the normal message path. */
  onSubmit: (message: string) => void;
  onDismiss: () => void;
  isDisabled?: boolean;
  /**
   * `floating` pins the drawer to the bottom of the nearest positioned
   * ancestor (the designer's chat panel). `inline` keeps it in the flow, just
   * above the composer (the intent refinement page).
   */
  variant?: 'inline' | 'floating';
};

/**
 * Bottom drawer that turns the agent's questions into taps instead of typing.
 *
 * One question at a time, options as radios with the last one a free-text row
 * the teacher can type straight into. Skip moves past a question they don't
 * care about; the last question submits every answer as one plain-text message.
 */
export function AgentQuestionDrawer({
  questions,
  onSubmit,
  onDismiss,
  isDisabled,
  variant = 'inline',
}: AgentQuestionDrawerProps) {
  const { t } = useTranslate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>({});
  const [otherTexts, setOtherTexts] = useState<Record<number, string>>({});
  // Lets a long option list be tucked away without fully dismissing the
  // question — the header (and its Expand control) stays put either way.
  const [isMinimized, setIsMinimized] = useState(false);

  // Same container renders both states so the drawer never closes and
  // reopens — it just swaps its body in place once the questions arrive.
  const isLoading = questions === null;
  // Memoized so its identity only changes with `questions` itself, rather
  // than handing the `answers` memo below a fresh `[]` on every render.
  const safeQuestions = useMemo(() => questions ?? [], [questions]);

  const answers = useMemo(() => {
    const result: Record<number, string> = {};
    safeQuestions.forEach((question, index) => {
      const selected = selectedOptions[index];
      // A question the agent asked without options is free text by definition.
      if (selected === OTHER_OPTION || question.options.length === 0) {
        result[index] = otherTexts[index] ?? '';
      } else if (selected) {
        result[index] = selected;
      }
    });
    return result;
  }, [safeQuestions, selectedOptions, otherTexts]);

  const currentQuestion = safeQuestions[currentIndex];
  const isLastQuestion = currentIndex === safeQuestions.length - 1;
  const hasCurrentAnswer = (answers[currentIndex] ?? '').trim().length > 0;

  const submit = (finalAnswers: Record<number, string>) => {
    const message = buildAgentQuestionsAnswerMessage(safeQuestions, finalAnswers);
    // Nothing answered leaves nothing to say — close the drawer rather than
    // sending an empty message.
    if (!message.trim()) {
      onDismiss();
      return;
    }
    onSubmit(message);
  };

  /** The primary button: on to the next question, or send what we have. */
  const commit = () => {
    if (isDisabled) return;
    if (!isLastQuestion) {
      setCurrentIndex(index => index + 1);
      return;
    }
    submit(answers);
  };

  /**
   * Skip means "no answer for this one", so it drops whatever is selected or
   * typed here — otherwise a half-typed "something else" would still be sent,
   * which is the opposite of what the button says. The other questions keep
   * their answers.
   */
  const skip = () => {
    if (isDisabled) return;
    setSelectedOptions(previous => {
      const next = { ...previous };
      delete next[currentIndex];
      return next;
    });
    setOtherTexts(previous => {
      const next = { ...previous };
      delete next[currentIndex];
      return next;
    });
    if (!isLastQuestion) {
      setCurrentIndex(index => index + 1);
      return;
    }
    // State updates land after this handler, so submit from an explicit copy
    // with this question cleared rather than from `answers`.
    submit({ ...answers, [currentIndex]: '' });
  };

  const selectOption = (option: string) =>
    setSelectedOptions(previous => ({ ...previous, [currentIndex]: option }));

  /** Pure navigation: moves between questions without touching any answer. */
  const goTo = (index: number) => {
    if (isDisabled) return;
    if (index < 0 || index > safeQuestions.length - 1) return;
    setCurrentIndex(index);
  };

  if (isLoading) {
    return (
      <DrawerContainer $variant={variant} $isLoading role="group">
        <DrawerHeader>
          <HeaderLeft>
            <DrawerTitle>
              <T keyName="AgentQuestionDrawer.Title" defaultValue="One quick question" />
            </DrawerTitle>
          </HeaderLeft>
          <DismissButton
            type="button"
            onClick={onDismiss}
            aria-label={t('Dismiss', 'Dismiss')}
          >
            <IconWrapper height="10px" width="10px" color={theme.colors.greyDark}>
              <CrossSvg />
            </IconWrapper>
          </DismissButton>
        </DrawerHeader>
        {/* Claims its share of the drawer's (now taller) height up front —
            filling it only once the question arrives would make the drawer
            visibly grow the instant it's done loading. */}
        <LoadingBody>
          <LoadingRow>
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 18, color: theme.colors.orange }}
                  spin
                />
              }
            />
            <T
              keyName="AgentQuestionDrawer.Preparing"
              defaultValue="Getting your question ready…"
            />
          </LoadingRow>
        </LoadingBody>
      </DrawerContainer>
    );
  }

  if (!currentQuestion) return null;

  const isOtherSelected =
    selectedOptions[currentIndex] === OTHER_OPTION ||
    currentQuestion.options.length === 0;

  return (
    <DrawerContainer $variant={variant} $isMinimized={isMinimized} role="group">
      <DrawerHeader>
        <HeaderLeft>
          {safeQuestions.length > 1 && (
            <StepNav>
              <StepArrow
                type="button"
                disabled={isDisabled || currentIndex === 0}
                onClick={() => goTo(currentIndex - 1)}
                aria-label={t(
                  'AgentQuestionDrawer.PreviousQuestion',
                  'Previous question',
                )}
              >
                <IconWrapper height="16px" width="16px" color={theme.colors.greyDark}>
                  <ArrowLeftSvg />
                </IconWrapper>
              </StepArrow>
              <StepArrow
                type="button"
                disabled={isDisabled || isLastQuestion}
                onClick={() => goTo(currentIndex + 1)}
                aria-label={t('AgentQuestionDrawer.NextQuestion', 'Next question')}
              >
                <IconWrapper
                  height="16px"
                  width="16px"
                  color={theme.colors.greyDark}
                  style={{ transform: 'rotate(180deg)' }}
                >
                  <ArrowLeftSvg />
                </IconWrapper>
              </StepArrow>
            </StepNav>
          )}
          <DrawerTitle>
            {safeQuestions.length > 1 ? (
              <T
                keyName="AgentQuestionDrawer.TitleProgress"
                defaultValue="Question {current} of {total}"
                params={{ current: currentIndex + 1, total: safeQuestions.length }}
              />
            ) : (
              <T keyName="AgentQuestionDrawer.Title" defaultValue="One quick question" />
            )}
          </DrawerTitle>
        </HeaderLeft>
        <HeaderRight>
          <MinimizeButton
            type="button"
            onClick={() => setIsMinimized(minimized => !minimized)}
            aria-label={
              isMinimized
                ? t('AgentQuestionDrawer.Expand', 'Expand')
                : t('AgentQuestionDrawer.Minimize', 'Minimize')
            }
          >
            <IconWrapper height="14px" width="14px" color={theme.colors.greyDark}>
              {isMinimized ? <ArrowUpSvg /> : <ArrowDownSvg />}
            </IconWrapper>
          </MinimizeButton>
          <DismissButton
            type="button"
            onClick={onDismiss}
            aria-label={t('Dismiss', 'Dismiss')}
          >
            <IconWrapper height="10px" width="10px" color={theme.colors.greyDark}>
              <CrossSvg />
            </IconWrapper>
          </DismissButton>
        </HeaderRight>
      </DrawerHeader>

      {!isMinimized && (
        <>
          {/* title gives a hover fallback for the full text once the clamp below cuts it off. */}
          <QuestionText title={currentQuestion.question}>
            {currentQuestion.question}
          </QuestionText>

          {/* Radio-styled buttons rather than <input type="radio">: the browser owns
              a native group's checked state and mutates siblings without telling
              React, which left the drawer showing an option that wasn't the answer
              being sent. Here the selection is React state and nothing else. */}
          <OptionsList role="radiogroup" aria-label={currentQuestion.question}>
            {currentQuestion.options.map(option => (
              <OptionRow
                key={option}
                type="button"
                role="radio"
                aria-label={option}
                aria-checked={selectedOptions[currentIndex] === option}
                disabled={isDisabled}
                $isSelected={selectedOptions[currentIndex] === option}
                onClick={() => selectOption(option)}
              >
                <RadioMark $isSelected={selectedOptions[currentIndex] === option} />
                <OptionLabel>{option}</OptionLabel>
              </OptionRow>
            ))}

            {/* The last row is always free text, typeable without selecting it
                first — the mark follows the typing. */}
            <OtherRow $isSelected={isOtherSelected}>
              {currentQuestion.options.length > 0 && (
                <RadioMarkButton
                  type="button"
                  role="radio"
                  aria-checked={isOtherSelected}
                  aria-label={t(
                    'AgentQuestionDrawer.OtherPlaceholder',
                    'Something else...',
                  )}
                  disabled={isDisabled}
                  onClick={() => selectOption(OTHER_OPTION)}
                >
                  <RadioMark $isSelected={isOtherSelected} />
                </RadioMarkButton>
              )}
              <OtherInput
                disabled={isDisabled}
                value={otherTexts[currentIndex] ?? ''}
                placeholder={
                  currentQuestion.options.length > 0
                    ? t('AgentQuestionDrawer.OtherPlaceholder', 'Something else...')
                    : t('AgentQuestionDrawer.AnswerPlaceholder', 'Type your answer...')
                }
                onChange={event => {
                  setOtherTexts(previous => ({
                    ...previous,
                    [currentIndex]: event.target.value,
                  }));
                  selectOption(OTHER_OPTION);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (hasCurrentAnswer) commit();
                  }
                }}
              />
            </OtherRow>
          </OptionsList>

          <DrawerActions>
            <Button type="ghost" disabled={isDisabled} onClick={skip}>
              <T keyName="AgentQuestionDrawer.Skip" defaultValue="Skip" />
            </Button>
            {/* Selecting an option never advances on its own — the teacher confirms
                each answer, and the last confirmation is the send. */}
            <Button
              type="orange"
              disabled={isDisabled || !hasCurrentAnswer}
              onClick={commit}
            >
              {isLastQuestion ? (
                <T keyName="AgentQuestionDrawer.Submit" defaultValue="Submit" />
              ) : (
                <T keyName="AgentQuestionDrawer.Next" defaultValue="Next" />
              )}
            </Button>
          </DrawerActions>
        </>
      )}
    </DrawerContainer>
  );
}

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DrawerContainer = styled.div<{
  $variant: 'inline' | 'floating';
  $isLoading?: boolean;
  $isMinimized?: boolean;
}>`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 22px 22px 22px;
  text-align: left;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px 10px 0 0;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  animation: ${slideUp} 0.25s ease-out;
  /* Bigger question text and options need more room than the old, denser
     layout did — reading comfortably here matters more than keeping the
     chat visible behind it, so this is allowed to grow past half the
     screen. */
  max-height: 60%;
  /* This container itself never scrolls — that let a long option list push
     the Skip/Next buttons below the fold, scrolled out of reach along with
     everything else. The header, question, and actions all opt out of
     shrinking below; only OptionsList (see its own styled component) gives
     up space and scrolls internally when it doesn't fit. */
  overflow: hidden;

  /* Positioned, not a plain flex child: sitting in flow would make the
     chat area's flex: 1 sibling shrink to make room for it, resizing —
     and visibly shifting — the chat scroll area every time the drawer
     opens or closes. Anchored to the bottom of the nearest positioned
     ancestor instead (the page container in the inline case; top: 0
     below covers the whole panel for floating), so the chat's height and
     scroll position never change regardless of the drawer.
     left/right, not width: 100% alone, so this still resolves against
     that ancestor's edges once taken out of flow. */
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;

  /* A viewport unit, not a %, so it doesn't need a definite-height ancestor
     to resolve. Only for inline: floating already fills its whole panel via
     top/bottom: 0 below, so a competing min-height there is redundant at
     best — and on a panel shorter than 50vh, could push this past its own
     top/bottom bounds. Claims real space immediately, while the question
     is still loading, instead of sizing to just a header and one spinner
     row, so the drawer doesn't visibly grow the moment it's done. */
  ${({ $isLoading, $variant }) =>
    $isLoading && $variant === 'inline' && `min-height: 50vh;`}

  ${({ $variant, $isMinimized }) =>
    $variant === 'floating' &&
    ($isMinimized
      ? `
    /* Minimized: shrink to just the header instead of stretching the whole
       panel via top: 0 below — otherwise the opaque body still blanks out
       the chat this control is supposed to reveal again. Falls back to the
       base rule's rounded card look (border/radius/shadow), same as the
       inline variant's minimized state. */
    z-index: 1002;
    justify-content: flex-end;
  `
      : `
    /* Covers the chat rather than sitting over its last messages: the agent asks
       the same questions in its reply, and reading them twice — once in the
       bubble, once in the drawer — is just noise. The questions sit at the bottom,
       nearest the composer. */
    top: 0;
    z-index: 1002;
    max-height: 100%;
    justify-content: flex-end;
    border-radius: 0;
    border: none;
    box-shadow: none;
  `)}

  @media (max-width: 992px) {
    padding: 16px 18px 18px 18px;

    /* A percentage of the positioned ancestor, same unit as the 60% above,
       just taller — not vh. Mobile Safari's vh is pinned to the *largest*
       possible viewport (as if its chrome were permanently hidden), so with
       the address bar visible 75vh measured taller than the actual visible
       screen and the drawer ran off the bottom. The ancestor here (the
       mobile page's fixed, top/bottom-anchored container — see
       IntentRefinementMobile's ContainerMobile) already tracks the real
       visible height, so a percentage of it does too. Inline only —
       floating already fills its whole panel via max-height: 100% below. */
    ${({ $variant }) => $variant === 'inline' && `max-height: 75%;`}
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const StepNav = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StepArrow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.paleBeige};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.orange};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const DrawerTitle = styled.span`
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.greyDark};
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.paleBeige};
  }
`;

const MinimizeButton = styled(DismissButton)``;

/* Grows to fill whatever space DrawerContainer's min-height claims, so the
   spinner+text row centers in it instead of hugging the header. */
const LoadingBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.greyDark};
`;

const QuestionText = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.45;
  color: ${theme.colors.black};
  flex-shrink: 0;
  /* Caps a very long question at two lines instead of pushing the options
     and Skip/Next off the bottom of the (non-scrolling) drawer — see the
     comment on DrawerContainer's overflow: hidden. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The one part of the drawer that scrolls. Shrink-only (`flex-grow: 0`) on
   purpose: the floating variant is pinned to `inset: 0` — the full designer
   panel — with `justify-content: flex-end` packing a short question to the
   bottom, leaving blank space above it. A growing OptionsList (flex-grow: 1)
   would swallow that blank space instead of leaving it for justify-content,
   stretching a two-option drawer to fill the whole panel. Shrink-only leaves
   slack alone and only gives up space when there isn't enough to begin with —
   `min-height: 0` is what lets it shrink below its content's natural height
   at all once that happens, scrolling in place rather than growing the whole
   drawer. */
const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const OptionRow = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 14px;
  text-align: left;
  background: none;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: ${theme.colors.black};

  &:hover:not(:disabled) {
    background-color: ${theme.colors.paleBeige};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.orange};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $isSelected }) => $isSelected && `font-weight: 700;`}
`;

const OtherRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 14px;
  border-radius: 10px;
  color: ${theme.colors.black};

  ${({ $isSelected }) => $isSelected && `font-weight: 700;`}
`;

const RadioMark = styled.span<{ $isSelected: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? theme.colors.orange : theme.colors.grey)};
  /* The dot, drawn with an inset shadow so no pseudo-element is needed. */
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? `inset 0 0 0 5.5px ${theme.colors.orange}` : 'none'};
`;

const RadioMarkButton = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${theme.colors.orange};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OptionLabel = styled.span`
  font-size: 18px;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const OtherInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 0;
  font-size: 18px;
  font-weight: inherit;
  color: ${theme.colors.black};
  background: none;
  border: none;
  border-bottom: 1.5px solid ${theme.colors.beigeDark};

  &::placeholder {
    font-weight: 400;
    color: ${theme.colors.greyDark};
  }

  &:focus {
    outline: none;
    border-bottom-color: ${theme.colors.orange};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const DrawerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;
